import React, { useState, useMemo, useContext } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Edit2,
  Trash2,
  CheckCircle2,
  Tv2,
  Home,
  Lightbulb,
  Car,
  Smartphone,
  Shield,
  UtensilsCrossed,
  AlertTriangle,
  Info,
  Zap,
} from 'lucide-react';
import AppModal from './Modal/AppModal';
import { expenseTemplates, type ExpenseTemplate } from '../data/expenseTemplates';
import { useCreateExpense } from '../hooks/useExpenses';
import { AppContext } from '../App';
import { useToast } from './ToastProvider/ToastProvider';

interface QuickAddModalProps {
  open: boolean;
  onClose: () => void;
}

interface SelectedTemplate extends ExpenseTemplate {
  customPrice?: number;
  customDueDay?: number;
  customWave?: 1 | 2;
}

const QuickAddModal: React.FC<QuickAddModalProps> = ({ open, onClose }) => {
  const context = useContext(AppContext);
  const toast = useToast();
  const [step, setStep] = useState<'select' | 'review'>('select');
  const [selectedTemplates, setSelectedTemplates] = useState<SelectedTemplate[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAddingExpenses, setIsAddingExpenses] = useState(false);

  if (!context) return null;

  const { token, t, language, wave1Amount, wave2Amount, expenses } = context;
  const createExpenseMutation = useCreateExpense(token);

  // Get existing expense names for filtering
  const existingExpenseNames = useMemo(() => {
    return new Set(
      (expenses || []).map((exp) => exp.name.toLowerCase().trim())
    );
  }, [expenses]);

  // Group templates by category and filter out already added
  const templatesByCategory = useMemo(() => {
    const categories = [
      'streaming',
      'housing',
      'utilities',
      'transportation',
      'subscriptions',
      'insurance',
      'food',
    ] as const;

    return categories.map((category) => ({
      category,
      templates: expenseTemplates
        .filter((t) => {
          // Filter by category
          if (t.category !== category) return false;

          // Check if expense already exists (match by name in both languages)
          const templateNameEn = t.name.toLowerCase().trim();
          const templateNameEs = t.nameEs.toLowerCase().trim();
          const isAlreadyAdded = existingExpenseNames.has(templateNameEn) ||
                                  existingExpenseNames.has(templateNameEs);

          return !isAlreadyAdded;
        })
        .sort((a, b) => a.priority - b.priority),
    }));
  }, [existingExpenseNames]);

  // Category icons and translations
  const categoryInfo: Record<
    string,
    { icon: React.ReactNode; label: string; color: string }
  > = {
    streaming: {
      icon: <Tv2 size={16} />,
      label: language === 'es' ? 'Streaming' : 'Streaming',
      color: '#007AFF',
    },
    housing: {
      icon: <Home size={16} />,
      label: language === 'es' ? 'Vivienda' : 'Housing',
      color: '#5AC8FA',
    },
    utilities: {
      icon: <Lightbulb size={16} />,
      label: language === 'es' ? 'Servicios' : 'Utilities',
      color: '#34C759',
    },
    transportation: {
      icon: <Car size={16} />,
      label: language === 'es' ? 'Transporte' : 'Transportation',
      color: '#FF9500',
    },
    subscriptions: {
      icon: <Smartphone size={16} />,
      label: language === 'es' ? 'Suscripciones' : 'Subscriptions',
      color: '#AF52DE',
    },
    insurance: {
      icon: <Shield size={16} />,
      label: language === 'es' ? 'Seguros' : 'Insurance',
      color: '#FF2D55',
    },
    food: {
      icon: <UtensilsCrossed size={16} />,
      label: language === 'es' ? 'Comida' : 'Food',
      color: '#FF3B30',
    },
  };

  const handleToggleTemplate = (template: ExpenseTemplate) => {
    setSelectedTemplates((prev) => {
      const exists = prev.find((t) => t.id === template.id);
      if (exists) {
        return prev.filter((t) => t.id !== template.id);
      } else {
        return [...prev, { ...template }];
      }
    });
  };

  const handleUpdateTemplate = (id: string, updates: Partial<SelectedTemplate>) => {
    setSelectedTemplates((prev) =>
      prev.map((t) => (t.id === id ? { ...t, ...updates } : t))
    );
    setEditingId(null);
  };

  const handleRemoveTemplate = (id: string) => {
    setSelectedTemplates((prev) => prev.filter((t) => t.id !== id));
  };

  const handleNext = () => {
    if (selectedTemplates.length === 0) {
      toast.warning(t.noTemplatesSelected || 'Please select at least one template.');
      return;
    }
    setStep('review');
  };

  const handleBack = () => {
    setStep('select');
  };

  const handleAddExpenses = async () => {
    // Validate required fields
    const missingPrice = selectedTemplates.filter(
      (t) => t.price === null && t.customPrice === undefined
    );

    if (missingPrice.length > 0) {
      toast.error(
        t.missingRequiredFields
          ? t.missingRequiredFields(missingPrice.length)
          : `${missingPrice.length} template(s) need an amount`
      );
      return;
    }

    setIsAddingExpenses(true);
    try {
      // Create all expenses
      const expensePromises = selectedTemplates.map((template) => {
        const price = template.customPrice ?? template.price ?? 0;
        const dueDay = template.customDueDay ?? template.suggestedDueDay;
        const wave = template.customWave ?? template.suggestedWave;

        // Calculate due date based on current date and due day
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth() + 1;
        const dueDate = `${year}-${String(month).padStart(2, '0')}-${String(dueDay).padStart(2, '0')}`;

        return createExpenseMutation.mutateAsync({
          name: language === 'es' ? template.nameEs : template.name,
          price,
          dueDate,
          category: 'Wave',
          wave,
          paymentType: template.paymentType || 'url',
          paymentUrl: template.paymentUrl || '',
          isRecurring: template.isRecurring,
          recurrenceFrequency: template.recurrenceFrequency,
          isPaid: false,
        });
      });

      await Promise.all(expensePromises);

      toast.success(
        t.templateAddedSuccess
          ? t.templateAddedSuccess(selectedTemplates.length)
          : `${selectedTemplates.length} expense(s) added successfully!`
      );

      // Reset and close
      setSelectedTemplates([]);
      setStep('select');
      onClose();
    } catch (error: any) {
      toast.error(error.message || 'Failed to add expenses');
    } finally {
      setIsAddingExpenses(false);
    }
  };

  const handleClose = () => {
    // Prevent closing while adding expenses
    if (isAddingExpenses) return;

    setSelectedTemplates([]);
    setStep('select');
    setEditingId(null);
    onClose();
  };

  return (
    <AppModal
      open={open}
      onClose={handleClose}
      title={t.quickAddCommonExpenses || 'Quick Add Common Expenses'}
      icon={<Zap size={20} />}
      maxWidth="md"
      fullWidth
      variant="default"
      actions={
        step === 'select'
          ? {
              primary: {
                label: t.reviewSelected || 'Review Selected',
                onClick: handleNext,
                startIcon: <ChevronRight size={18} />,
                disabled: selectedTemplates.length === 0,
              },
            }
          : {
              secondary: {
                label: t.backToTemplates || 'Back',
                onClick: handleBack,
                startIcon: <ChevronLeft size={18} />,
                variant: 'outlined',
                disabled: isAddingExpenses,
              },
              primary: {
                label: isAddingExpenses
                  ? `${t.loading || 'Adding'}...`
                  : t.addSelected
                  ? t.addSelected(selectedTemplates.length)
                  : `Add Selected (${selectedTemplates.length})`,
                onClick: handleAddExpenses,
                startIcon: <CheckCircle2 size={18} />,
                disabled: selectedTemplates.length === 0 || isAddingExpenses,
              },
            }
      }
    >
      <div className="py-2 space-y-4">
        {/* Step Indicator */}
        <div className="flex justify-center gap-2 mb-4">
          {[
            { key: 'select', label: t.selectTemplates || '1. Select Templates' },
            { key: 'review', label: t.reviewAndCustomize || '2. Review & Customize' },
          ].map(({ key, label }) => (
            <span
              key={key}
              className={`px-3 py-1 rounded-chip text-xs font-semibold ${
                step === key
                  ? 'bg-primary text-white'
                  : 'border border-white/20 text-[var(--text-secondary)]'
              }`}
            >
              {label}
            </span>
          ))}
        </div>

        {/* Description */}
        <p className="text-sm text-center text-[var(--text-secondary)]">
          {t.quickAddDescription || 'Select from popular expenses and add them instantly'}
        </p>

        {/* Info banners */}
        {templatesByCategory.every(cat => cat.templates.length === 0) && (
          <div className="flex items-start gap-2 p-3 rounded-xl bg-primary/10 border border-primary/20 text-sm text-[var(--text-color)]">
            <Info size={16} className="text-primary flex-shrink-0" />
            <span>{t.allTemplatesAdded || 'All common expenses have been added! You can add custom expenses using the regular form.'}</span>
          </div>
        )}
        {selectedTemplates.length > 0 && (
          <div className="flex items-start gap-2 p-3 rounded-xl bg-primary/10 border border-primary/20 text-sm text-[var(--text-color)]">
            <Info size={16} className="text-primary flex-shrink-0" />
            <span>
              {t.templatesSelected
                ? t.templatesSelected(selectedTemplates.length)
                : `${selectedTemplates.length} template${selectedTemplates.length > 1 ? 's' : ''} selected`}
            </span>
          </div>
        )}

        {/* STEP 1: Template Selection */}
        {step === 'select' && (
          <div className="space-y-4">
            {templatesByCategory.map(({ category, templates }) => {
              if (templates.length === 0) return null;
              const info = categoryInfo[category];
              return (
                <div key={category}>
                  <div className="flex items-center gap-2 mb-2 pb-1" style={{ borderBottom: `2px solid ${info.color}` }}>
                    <h6 className="text-base font-semibold text-[var(--text-color)]">{info.icon} {info.label}</h6>
                    <span className="px-1.5 py-0.5 rounded-full text-xs font-bold text-white" style={{ backgroundColor: info.color }}>
                      {templates.length}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1">
                    {templates.map((template) => {
                      const isSelected = selectedTemplates.some((t) => t.id === template.id);
                      return (
                        <div
                          key={template.id}
                          onClick={() => handleToggleTemplate(template)}
                          className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all hover:-translate-y-0.5 ${
                            isSelected ? 'border-primary/30 bg-primary/8' : 'border-white/10 bg-white/[0.02] hover:border-white/20'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}}
                            onClick={(e) => e.stopPropagation()}
                            className="w-4 h-4 accent-primary flex-shrink-0"
                          />
                          <span className="text-2xl leading-none">{template.icon}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-[var(--text-color)]">
                              {language === 'es' ? template.nameEs : template.name}
                            </p>
                            <p className="text-xs text-[var(--text-secondary)] truncate">
                              {template.isRecurring && `${
                                template.recurrenceFrequency === 'monthly' ? (language === 'es' ? 'Mensual' : 'Monthly')
                                : template.recurrenceFrequency === 'biweekly' ? (language === 'es' ? 'Quincenal' : 'Biweekly')
                                : (language === 'es' ? 'Semanal' : 'Weekly')
                              } · `}
                              {language === 'es' ? 'Ola' : 'Wave'} {template.suggestedWave} · {language === 'es' ? 'Día' : 'Day'} {template.suggestedDueDay}
                            </p>
                          </div>
                          <span className={`text-base font-bold ${template.price === null ? 'text-[var(--text-secondary)]' : 'text-primary'}`}>
                            {template.price === null ? t.enterAmount || 'Enter amount' : `$${template.price.toFixed(2)}`}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* STEP 2: Review & Customize */}
        {step === 'review' && (
          <div className="space-y-3">
            {selectedTemplates.length === 0 ? (
              <div className="flex items-start gap-2 p-3 rounded-xl bg-orange/10 border border-orange/20 text-sm text-[var(--text-color)]">
                <AlertTriangle size={16} className="text-orange flex-shrink-0" />
                <span>{t.noTemplatesSelected || 'No templates selected'}</span>
              </div>
            ) : selectedTemplates.map((template) => {
              const isEditing = editingId === template.id;
              const price = template.customPrice ?? template.price;
              const needsPrice = price === null || price === undefined;
              return (
                <div
                  key={template.id}
                  className={`p-3 rounded-xl border ${needsPrice ? 'border-red/50 bg-red/5' : 'border-white/10 bg-white/[0.02]'}`}
                >
                  {/* Header row */}
                  <div className={`flex items-center gap-3 ${isEditing ? 'mb-3' : ''}`}>
                    <span className="text-2xl leading-none">{template.icon}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[var(--text-color)]">
                        {language === 'es' ? template.nameEs : template.name}
                      </p>
                      {needsPrice && (
                        <p className="text-xs font-semibold text-red">
                          {t.amountRequired || '⚠ Amount required'}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => setEditingId(isEditing ? null : template.id)}
                      className="p-1 rounded-lg text-primary hover:bg-primary/10 transition-colors"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleRemoveTemplate(template.id)}
                      className="p-1 rounded-lg text-red hover:bg-red/10 transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  {/* Edit Form */}
                  {isEditing && (
                    <div className="grid gap-3 pt-3 border-t border-white/10" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-[var(--text-secondary)]">{t.amount || 'Amount'}</label>
                        <div className={`flex items-center rounded-input border text-sm bg-white/5 ${needsPrice ? 'border-red' : 'border-white/10'}`}>
                          <span className="pl-3 text-[var(--text-secondary)]">$</span>
                          <input
                            type="number"
                            value={price ?? ''}
                            onChange={(e) => handleUpdateTemplate(template.id, { customPrice: parseFloat(e.target.value) })}
                            className="flex-1 bg-transparent px-2 py-2 text-[var(--text-color)] outline-none"
                            required
                          />
                        </div>
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-[var(--text-secondary)]">{t.dueDayHeader || 'Due Day'}</label>
                        <input
                          type="number"
                          value={template.customDueDay ?? template.suggestedDueDay}
                          min={1} max={31}
                          onChange={(e) => handleUpdateTemplate(template.id, { customDueDay: parseInt(e.target.value) as any })}
                          className="rounded-input border border-white/10 bg-white/5 px-3 py-2 text-sm text-[var(--text-color)] outline-none focus:border-primary"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-[var(--text-secondary)]">{language === 'es' ? 'Ola' : 'Wave'}</label>
                        <select
                          value={template.customWave ?? template.suggestedWave}
                          onChange={(e) => handleUpdateTemplate(template.id, { customWave: Number(e.target.value) as 1 | 2 })}
                          className="rounded-input border border-white/10 bg-[#1a1a1a] px-3 py-2 text-sm text-[var(--text-color)] outline-none focus:border-primary"
                        >
                          <option value={1}>{language === 'es' ? 'Ola 1' : 'Wave 1'} (${wave1Amount?.toFixed(2)})</option>
                          <option value={2}>{language === 'es' ? 'Ola 2' : 'Wave 2'} (${wave2Amount?.toFixed(2)})</option>
                        </select>
                      </div>
                    </div>
                  )}

                  {/* Display chips (when not editing) */}
                  {!isEditing && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      <span className={`px-2 py-0.5 rounded-chip text-xs font-semibold ${needsPrice ? 'bg-red/10 text-red border border-red/20' : 'bg-primary/10 text-primary border border-primary/20'}`}>
                        {needsPrice ? t.enterAmount || 'Enter amount' : `$${price?.toFixed(2)}`}
                      </span>
                      <span className="px-2 py-0.5 rounded-chip text-xs border border-white/15 text-[var(--text-secondary)]">
                        {language === 'es' ? 'Día' : 'Day'} {template.customDueDay ?? template.suggestedDueDay}
                      </span>
                      <span className="px-2 py-0.5 rounded-chip text-xs border border-white/15 text-[var(--text-secondary)]">
                        {language === 'es' ? 'Ola' : 'Wave'} {template.customWave ?? template.suggestedWave}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppModal>
  );
};

export default QuickAddModal;
